import { Box, Flex, Icon, Image, Tag, Text } from '@chakra-ui/react';
import { HomeIcon } from 'lucide-react';
import { FC } from 'react';
import { PropertySummary } from './types';

interface PropertySummaryCardProps {
  property: PropertySummary | null | undefined;
  /** Fallback title when the property has no street address. */
  folderName: string;
}

/**
 * Maps a raw `transactionSide` value to a human label, or `null` when the side
 * is unknown/absent (in which case the chip is hidden).
 */
const formatTransactionSide = (
  side: string | null | undefined
): string | null => {
  switch (side?.toUpperCase()) {
    case 'BUY':
      return 'Buy';
    case 'SELL':
      return 'Sell';
    default:
      return null;
  }
};

/**
 * Left-rail property header for the agent compliance review: thumbnail,
 * address (falling back to the folder name), muted city/state line, and chips
 * for transaction side and property type.
 */
export const PropertySummaryCard: FC<PropertySummaryCardProps> = ({
  property,
  folderName,
}) => {
  const title = property?.streetAddress?.trim() || folderName;
  const cityState = property?.cityState?.trim();
  const sideLabel = formatTransactionSide(property?.transactionSide);
  const propertyType = property?.propertyType?.trim() || null;

  return (
    <Flex align="center" gap={3}>
      {property?.imageUrl ? (
        <Image
          alt={title}
          borderRadius="md"
          boxSize="48px"
          flexShrink={0}
          objectFit="cover"
          src={property.imageUrl}
        />
      ) : (
        <Flex
          align="center"
          bg="compliance.document.bg"
          borderRadius="md"
          boxSize="48px"
          flexShrink={0}
          justify="center"
        >
          <Icon
            as={HomeIcon}
            boxSize="20px"
            color="compliance.icon.secondary"
          />
        </Flex>
      )}

      <Flex direction="column" gap={1} overflow="hidden">
        <Box overflow="hidden">
          <Text
            color="compliance.text.primary"
            fontSize="md"
            fontWeight="semibold"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {title}
          </Text>
          {cityState && (
            <Text color="compliance.text.muted" fontSize="sm">
              {cityState}
            </Text>
          )}
        </Box>

        {(sideLabel || propertyType) && (
          <Flex gap={2} wrap="wrap">
            {sideLabel && (
              <Tag
                bg="compliance.tag.bg"
                borderRadius="full"
                color="compliance.tag.text"
                size="sm"
              >
                {sideLabel}
              </Tag>
            )}
            {propertyType && (
              <Tag
                bg="compliance.tag.bg"
                borderRadius="full"
                color="compliance.tag.text"
                size="sm"
              >
                {propertyType}
              </Tag>
            )}
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};
